import XLSX from "sheetjs-style";
import { childAge, fmtDate, fmtDateTime } from "./crmService";

const styleHeader = (ws, count) => {
  for (let c = 0; c < count; c += 1) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
    if (cell) cell.s = { font: { bold: true }, fill: { fgColor: { rgb: "EFEFEF" } } };
  }
};

const sheet = (header, rows, widths) => {
  const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
  ws["!cols"] = widths.map((wch) => ({ wch }));
  styleHeader(ws, header.length);
  return ws;
};

/** Online enquiries: the public form's own answers in one cell ("Label: value; ..."). */
const onlineAnswers = (e) => {
  const f = e.onlineForm;
  if (!f) return "";
  return [
    ["Father", [f.fatherName, f.fatherContactNumber].filter(Boolean).join(" ")],
    ["Mother", [f.motherName, f.motherContactNumber].filter(Boolean).join(" ")],
    ["Class / age group", f.classOrAgeGroup],
    ["Admission sought for", f.admissionSoughtFor],
    ...(e.customFields || []).map((c) => [c.label || "Custom field", c.value]),
  ]
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
};

/** Two sheets: the filtered enquiries and their follow-up history. */
export function downloadEnquiriesExcel({ items, followUps }, tz) {
  const enquiries = sheet(
    [
      "Enquiry ID", "Enquiry date", "Parent / guardian", "Relationship", "Mobile", "Alternate phone", "Email",
      "Child name", "Date of birth", "Age", "Gender", "Program", "Academic year", "Current school", "Area / locality",
      "Address", "Source", "Source detail", "Assigned to", "Status", "Lost reason", "Next follow-up",
      "Last follow-up", "Notes", "Added by", "Online form answers",
    ],
    items.map((e) => [
      e.enquiryNo,
      fmtDate(e.enquiryDate, tz),
      e.parentName,
      e.relationship,
      e.mobile,
      e.alternatePhone,
      e.email,
      e.childName,
      fmtDate(e.childDob, tz),
      childAge(e),
      e.gender,
      e.program,
      e.academicYear,
      e.currentSchool,
      e.area,
      e.address,
      e.source,
      e.sourceOther || e.referrerName || e.siblingStudentName || "",
      e.assignedTo,
      e.status,
      e.lostReason,
      fmtDateTime(e.nextFollowUpDate, tz),
      fmtDate(e.lastFollowUpAt, tz),
      e.notes,
      e.createdBy?.name || "",
      onlineAnswers(e),
    ]),
    [12, 13, 22, 12, 15, 15, 24, 18, 13, 8, 8, 18, 12, 20, 18, 28, 20, 20, 16, 20, 18, 20, 14, 40, 16, 50]
  );
  const history = sheet(
    ["Enquiry ID", "Parent / guardian", "Child", "Date", "Type", "Outcome", "Status before", "Status after", "Lost reason", "Next follow-up", "Notes", "Done by"],
    followUps.map((f) => [
      f.enquiryNo,
      f.parentName,
      f.childName,
      fmtDate(f.at, tz),
      f.kind === "status" ? "Status change" : f.type,
      f.outcome,
      f.statusBefore,
      f.statusAfter,
      f.lostReason,
      fmtDateTime(f.nextFollowUpDate, tz),
      f.notes,
      f.staffName || f.doneBy?.name || "",
    ]),
    [12, 22, 18, 13, 18, 16, 20, 20, 18, 20, 40, 16]
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, enquiries, "Enquiries");
  XLSX.utils.book_append_sheet(wb, history, "Follow-ups");
  XLSX.writeFile(wb, `admissions-enquiries-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

/** Import fields, in template order. `aliases` help auto-match a file's headers. */
export const IMPORT_FIELDS = [
  { key: "parentName", label: "Parent / guardian name", aliases: ["parent", "parent name", "guardian", "name", "father name", "mother name"] },
  { key: "mobile", label: "Mobile", required: true, aliases: ["mobile", "phone", "mobile number", "phone number", "contact", "contact number"] },
  { key: "alternatePhone", label: "Alternate phone", aliases: ["alternate", "alternate phone", "alt phone", "phone 2"] },
  { key: "email", label: "Email", aliases: ["email", "email id", "e-mail"] },
  { key: "relationship", label: "Relationship", aliases: ["relationship", "relation"] },
  { key: "childName", label: "Child name", aliases: ["child", "child name", "student", "student name", "kid"] },
  { key: "childDob", label: "Date of birth", aliases: ["dob", "date of birth", "birth date"] },
  { key: "childAgeYears", label: "Child age (years)", aliases: ["age", "child age"] },
  { key: "gender", label: "Gender", aliases: ["gender", "sex"] },
  { key: "program", label: "Program", aliases: ["program", "programme", "class", "grade"] },
  { key: "academicYear", label: "Academic year", aliases: ["academic year", "year"] },
  { key: "currentSchool", label: "Current school", aliases: ["current school", "day care", "daycare", "school"] },
  { key: "area", label: "Area / locality", aliases: ["area", "locality", "location"] },
  { key: "address", label: "Address", aliases: ["address"] },
  { key: "source", label: "Source", aliases: ["source", "lead source"] },
  { key: "assignedTo", label: "Assigned to", aliases: ["assigned", "assigned to", "owner", "staff"] },
  { key: "enquiryDate", label: "Enquiry date", aliases: ["enquiry date", "date", "inquiry date"] },
  { key: "nextFollowUpDate", label: "Next follow-up", aliases: ["next follow-up", "next follow up", "follow up date", "follow-up date"] },
  { key: "notes", label: "Notes", aliases: ["notes", "remarks", "comments", "comment"] },
];

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Best guess: file header → field key ("" = skip). */
export function autoMap(headers) {
  const used = new Set();
  return headers.map((h) => {
    const n = norm(h);
    const field = IMPORT_FIELDS.find(
      (f) => !used.has(f.key) && (norm(f.label) === n || f.aliases.some((a) => norm(a) === n))
    );
    if (field) used.add(field.key);
    return field ? field.key : "";
  });
}

const pad = (n) => String(n).padStart(2, "0");

/** Cell → string; Excel dates become YYYY-MM-DD. */
export function cellValue(v) {
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  return v == null ? "" : String(v).trim();
}

/** Read the first sheet: { headers, rows (arrays of strings) }. */
export async function readSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "", blankrows: false });
  const [head = [], ...body] = aoa;
  const headers = head.map((h, i) => String(h || "").trim() || `Column ${i + 1}`);
  const rows = body
    .map((r, i) => ({ cells: headers.map((_, c) => cellValue(r[c])), rowNo: i + 2 }))
    .filter((r) => r.cells.some(Boolean));
  return { headers, rows };
}

export function downloadImportTemplate() {
  const ws = sheet(
    IMPORT_FIELDS.map((f) => f.label),
    [["Jenny", "9876543210", "", "jenny@example.com", "Mother", "Ava", "2023-03-15", "", "Female", "Primary 3–6", "2026-27", "", "Indiranagar", "", "Social Media", "", "", "", "Prefers mornings"]],
    IMPORT_FIELDS.map(() => 18)
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
  XLSX.writeFile(wb, "admissions-import-template.xlsx");
}
